import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContent, articleEntries } from "@/mocks/articles";
import { ArticleSchema, BreadcrumbSchema, FAQPageSchema } from "@/components/base/SchemaOrg";
import usePageSEO from "@/hooks/usePageSEO";

const SITE_DOMAIN = typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env ? ((import.meta as Record<string, Record<string, string>>).env.VITE_SITE_URL ?? "https://sugargoospreadsheet.delivery") : "https://sugargoospreadsheet.delivery";

function getArticleBySlug(slug: string) {
  return articleEntries.find((e) => e.slug === `/guide/${slug}`) || articleEntries[0];
}

function buildSEODescription(article: (typeof articleEntries)[0]): string {
  return article.description.length > 160
    ? article.description.slice(0, 157) + "..."
    : article.description;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const resolvedSlug = slug || "what-is-sugargoo-spreadsheet";
  const currentArticle = getArticleBySlug(resolvedSlug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [resolvedSlug]);

  const seoDescription = buildSEODescription(currentArticle);
  usePageSEO({
    title: `${currentArticle.title} | Sugargoo Spreadsheet`,
    description: seoDescription,
    canonicalPath: currentArticle.slug,
  });

  const isFAQArticle = resolvedSlug === "sugargoo-spreadsheet-faq";
  const relatedGuides = articleEntries
    .filter((e) => e.slug !== currentArticle.slug)
    .slice(0, 4);

  const faqQuestions = [
    { question: "Is a Sugargoo Spreadsheet free?", answer: "Absolutely. Since most users build theirs in Google Sheets, the only cost is a few minutes of your time to set it up. Many community members share free templates that you can copy and customize to fit your needs." },
    { question: "Do I need to be technical to use one?", answer: "Not at all. If you can copy and paste a link and type in a few numbers, you can use a Sugargoo Spreadsheet. The most advanced thing you might do is use a simple SUM formula to add up your total spending, which takes about ten seconds to learn." },
    { question: "Can I use it on mobile?", answer: "Yes — Google Sheets has excellent mobile apps for both iOS and Android, so you can update your spreadsheet from anywhere. This is particularly useful when you spot an item while browsing on your phone." },
    { question: "Is my data safe?", answer: "Your spreadsheet lives in your own Google account, protected by the same security that guards your emails and documents. You control who has access and what level of access they receive. For additional peace of mind, you can enable two-factor authentication on your Google account." },
    { question: "How is this different from just using the Sugargoo app?", answer: "The app gives you a standardized view of your orders, which works well for basic tracking. The spreadsheet gives you a fully customizable view that you can adapt to your exact workflow. Most experienced users use both — the app for quick checks and the spreadsheet for detailed planning and record-keeping." },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema
        headline={currentArticle.title}
        description={seoDescription}
        url={`${SITE_DOMAIN}${currentArticle.slug}`}
        datePublished="2026-06-17"
        dateModified="2026-06-17"
        authorName="Sugargoo Spreadsheet Editorial Team"
        publisherName="Sugargoo Spreadsheet"
      />
      <BreadcrumbSchema
        items={[
          { name: "Sugargoo Spreadsheet", url: `${SITE_DOMAIN}/` },
          { name: currentArticle.title, url: `${SITE_DOMAIN}${currentArticle.slug}` },
        ]}
      />
      {isFAQArticle && (
        <FAQPageSchema
          questions={faqQuestions}
          url={`${SITE_DOMAIN}${currentArticle.slug}`}
        />
      )}

      <Navbar />

      <main>
        <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
              <div className="w-full lg:w-3/5">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer mb-6"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Sugargoo Spreadsheet Home
                </Link>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                  {currentArticle.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  {articleContent.date} &middot; By {articleContent.author}
                </p>
              </div>

              <div className="w-full lg:w-2/5 lg:pt-2">
                <div className="bg-secondary-50 rounded-2xl p-6 border border-background-200/70">
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Quick Navigation</p>
                  <div className="flex flex-col gap-2">
                    {articleContent.sections.map((section, idx) => (
                      <span key={idx} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                        {section.heading}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-background-200/70">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-home-4-line"></i>
                      Back to Sugargoo Spreadsheet Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
              <div className="w-full lg:w-3/5">
                <article>
                  {articleContent.sections.map((section, idx) => (
                    <div key={idx} className="mb-10 md:mb-14">
                      <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">
                        {section.heading}
                      </h2>
                      <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                        {section.content.split("\n\n").map((paragraph, pIdx) => (
                          <p key={pIdx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="mt-12 p-6 bg-primary-50 rounded-2xl border border-primary-100">
                    <p className="text-sm md:text-base text-foreground-800 leading-relaxed">
                      <strong className="text-primary-600">Ready to start shopping smarter?</strong> Visit the{" "}
                      <Link to="/" className="text-primary-500 font-semibold hover:underline cursor-pointer">
                        Sugargoo Spreadsheet home page
                      </Link>{" "}
                      to browse all 15 product categories, explore expert guides, and discover why thousands of shoppers trust the spreadsheet approach for managing their orders. You can also visit{" "}
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-primary-500 font-semibold hover:underline cursor-pointer"
                      >
                        our main store
                      </a>{" "}
                      to start shopping directly.
                    </p>
                  </div>
                </article>

                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Related Guides</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {relatedGuides.map((guide, idx) => (
                      <Link
                        key={idx}
                        to={guide.slug}
                        className="block p-4 bg-secondary-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <p className="text-sm font-semibold text-foreground-800">{guide.title}</p>
                        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{guide.description}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="hidden lg:block w-full lg:w-2/5">
                <div className="sticky top-24">
                  <div className="bg-accent-50 rounded-2xl p-6 border border-accent-100">
                    <p className="text-xs font-semibold text-accent-600 uppercase tracking-wider mb-3">Did You Know?</p>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      Users who maintain a Sugargoo Spreadsheet report saving an average of 15-30% on international shipping costs by optimizing item groupings and carrier selection.
                    </p>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Try it yourself
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>

                  <div className="mt-4 bg-background-100 rounded-2xl p-6 border border-background-200/70">
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Explore Categories</p>
                    <div className="flex flex-wrap gap-2">
                      {["Shoes", "Hoodies", "T-Shirts", "Jackets", "Bags", "Accessories"].map((cat) => (
                        <a
                          key={cat}
                          href="https://kaspreadsheet.com/"
                          target="_blank"
                          rel="noopener nofollow"
                          className="px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-50 rounded-full border border-background-200/70 hover:border-primary-200 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
                        >
                          {cat}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-home-4-line mr-2"></i>
                      Sugargoo Spreadsheet Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-secondary-50">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Ready to Explore More?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6">
              Head back to the Sugargoo Spreadsheet homepage to browse all product categories, discover more guides, and start shopping smarter today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center px-7 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line mr-2"></i>
                Sugargoo Spreadsheet Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-semibold rounded-full hover:border-primary-300 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
              >
                Visit Main Store
                <i className="ri-external-link-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}